import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FuturePathContext = createContext(null);

/**
 * The canonical FuturePath Context Profile, resolved on the server.
 * No screen decides for itself who the user is any more:
 *
 *   const { isMinor, mayAsk, askInstead, known, stageLabel } = useFPContext();
 *   {mayAsk('current_income') ? <IncomeField /> : <p>{askInstead('current_income')}</p>}
 */
export const FuturePathProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user || !token) { setCtx(null); setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/context/profile`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
      });
      setCtx(r.data);
    } catch {
      setCtx(null);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => {
    const asks = ctx?.asks || {};
    const knownMap = ctx?.known || {};
    const hidden = new Set((ctx?.modules?.hidden || []).map(m => m.key));
    return {
      loading,
      ctx,
      refresh,
      ready: !!ctx,
      identity: ctx?.identity || {},
      firstName: ctx?.identity?.first_name || '',
      age: ctx?.identity?.age ?? null,
      isMinor: !!ctx?.identity?.is_minor,
      stage: ctx?.stage?.key || 'unknown',
      stageLabel: ctx?.stage?.label || '',
      stageConfidence: ctx?.stage?.confidence ?? 0,
      studentStatus: ctx?.stage?.student_status || 'unknown',
      traits: ctx?.traits || {},
      entitlements: ctx?.entitlements || {},
      can: (key) => ctx?.entitlements?.[key] !== false,
      archetype: ctx?.archetype || {},
      institution: ctx?.institution || null,
      acting: ctx?.acting || {},
      conflicts: ctx?.conflicts || [],
      // May we ask this person this? Unknown keys default to yes — the gate is a
      // deliberate list of things that go wrong, not a whitelist of everything.
      mayAsk: (key) => (asks[key] ? asks[key].allowed : true),
      askReason: (key) => asks[key]?.reason || null,
      askInstead: (key) => asks[key]?.instead || null,
      // Anything already known is never asked again.
      known: (key) => knownMap[key]?.value ?? null,
      hasKnown: (key) => knownMap[key] !== undefined,
      knownSource: (key) => knownMap[key]?.source || null,
      allKnown: knownMap,
      missing: ctx?.missing || [],
      moduleHidden: (key) => hidden.has(key),
      // Phrases that break when a page assumes a life the person isn't living.
      phrase: (key, fallback = '') => (ctx?.phrases?.[key] ?? fallback),
      phrases: ctx?.phrases || {},
    };
  }, [ctx, loading, refresh]);

  return <FuturePathContext.Provider value={value}>{children}</FuturePathContext.Provider>;
};

export const useFPContext = () => useContext(FuturePathContext) || {
  loading: false, ctx: null, ready: false, refresh: async () => {},
  identity: {}, firstName: '', age: null, isMinor: false,
  stage: 'unknown', stageLabel: '', stageConfidence: 0, studentStatus: 'unknown',
  traits: {}, archetype: {}, institution: null, acting: {}, conflicts: [],
  entitlements: {}, can: () => true,
  mayAsk: () => true, askReason: () => null, askInstead: () => null,
  known: () => null, hasKnown: () => false, knownSource: () => null, allKnown: {},
  missing: [], moduleHidden: () => false, phrase: (_k, f = '') => f, phrases: {},
};
